import React, { useEffect, useState, FC } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import {Link, useNavigate, useParams} from "react-router-dom";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import BoxContent from "shared/ui/BoxContent";
import PageTitle from "shared/ui/PageTitle";
import { APIGetClassroom } from "features/classrooms/api/classroom";
import Loading from "shared/ui/Loading";
import { useAuth } from "hooks/useAuth";
import { APIUpdateClassroom, APIPostClassroom } from "features/classrooms/api/classroom";
import FormField from "shared/form/FormField";
import ErrorsList from "shared/ui/ErrorsList";
import { useLocation } from "react-router-dom";
import {getRomanNumber, gradesRomans, useRouteMatch} from "utils/functions";
import FormLabelBox from "shared/ui/FormLabelBox";
import FormFieldBox from "shared/ui/FormFieldBox";
import GenericButton from "shared/ui/GenericButton";
import CustomButton from "../../../shared/ui/CustomButton";
import DashboardStatistics from "../../dashboard/components/DashboardStatistics";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TestsHistory from "../../tests/listing/components/TestsHistory";
import ReportsList from "../../reports/shared/ReportsList";
import StudentsList from "../components/StudentsList";
import TeachersList from "../components/TeachersList";
import {useTheme} from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import CustomToolbar from "../../../shared/ui/CustomToolbar";

interface IFormClassroomEdit {
    name: string;
    grade: number;
}

interface IClassroom {
    id: string;
    name: string;
    grade: number;
    status: number;
    teachers: any[];
    students: any[];
}

const schema = yup
    .object({
    })
    .required();

const Classroom: FC = () => {
    const { id = "", action } = useParams();
    const { update, user, tenant } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const currentTab = useRouteMatch([`/clase/${id}`, `/clase/${id}/elevi`, `/clase/${id}/profesori`, `/clase/${id}/teste`, `/clase/${id}/rapoarte`]);
    const theme = useTheme();
    const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));

    const methods = useForm<IFormClassroomEdit>({
        resolver: yupResolver(schema),
    });

    const [loading, setLoading] = useState<boolean>(true);
    const [editMode, setEditMode] = useState<boolean>(false);
    const [classroom, setClassroom] = useState<IClassroom>({
        id: "",
        name: "",
        grade: -1,
        status: 1,
        teachers: [],
        students: [],
    });
    const [errorMessage, setErrorMessage] = useState<string>("");

    useEffect(() => {
        if (id !== "") {
            setLoading(true);
            APIGetClassroom({ id: Number(id) })
                .then((response) => {
                    if (response.data.success && response.data.data !== null) {
                        // console.log("getClassroom OK", response);
                        setClassroom(response.data.data.item);
                        setLoading(false);
                    } else if (response.data.success && response.data.data === null) {
                        navigate("/clase", { replace: true });
                    } else {
                        update("error", { show: true, code: "A002" });
                    }
                })
                .catch((err) => {
                    // console.log("getClassroom ERR", err);
                    update("error", { show: true, code: "A001" });
                });
        } else {
            let locationArray = location.pathname.split("/");
            if (locationArray[locationArray.length - 1] === "adauga") {
                setEditMode(true);
                setLoading(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (id !== "") {
            methods.clearErrors();
            setEditMode(action === "editare");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action, id]);

    const updateClassroomHandler = (newClassroom: any) => {
        setClassroom(newClassroom);
    }

    const onSubmit: SubmitHandler<IFormClassroomEdit> = (data) => {
        let classroomTeachers: number[] = classroom.teachers ? classroom.teachers.map(({ id }: any) => id) : [];
        let classroomStudents: number[] = classroom.students ? classroom.students.map(({ id }: any) => id) : [];
        const users = [...classroomTeachers, ...classroomStudents];

        const classroomGrade = data.grade;

        if (
            data.name !== classroom.name ||
            classroomGrade !== classroom.grade
        ) {
            methods.clearErrors();
            setErrorMessage("");
            update("loading", { show: true, modal: true });
            if (id !== "") {
                APIUpdateClassroom(id, {
                    name: data.name,
                    grade: classroomGrade,
                    users: users,
                    status: classroom.status,
                })
                    .then((response) => {
                        if (response.data.success) {
                            // console.log("updateClassroom OK", response);
                            const updateClassroom = {
                                ...classroom,
                                ...{ name: data.name, grade:  classroomGrade},
                            };

                            setClassroom(updateClassroom);

                            update("loading", { show: false });
                            navigate(-1);
                        } else {
                            update("loading", { show: false });
                            update("error", { show: true, code: "A002" });
                        }
                    })
                    .catch((err) => {
                        // console.log("updateClassroom ERR", err);
                        if (err.response.status === 400) {
                            let errors = err.response.data.data;
                            if (errors.name) {
                                methods.setError("name", { type: "server", message: errors.name[0] });
                            }
                            if (errors.grade) {
                                methods.setError("grade", { type: "server", message: errors.grade[0] });
                            }
                            update("loading", { show: false });
                        } else {
                            update("error", { show: true, code: "A001" });
                        }
                    });
            } else {
                APIPostClassroom({
                    name: data.name,
                    grade: classroomGrade,
                    users: users,
                    status: 1,
                })
                    .then((response) => {
                        if (response.data.success) {
                            // console.log("postClassroom OK", response);
                            setClassroom(prevState => ({...prevState, name: data.name, grade: classroomGrade, id: response.data.data.item.id}));
                            setEditMode(false);
                            navigate(`/clase/${response.data.data.item.id}`, { replace: true });
                            update("loading", { show: false });
                        } else {
                            update("loading", { show: false });
                            update("error", { show: true, code: "A002" });
                        }
                    })
                    .catch((err) => {
                        // console.log("postClassroom ERR", err);
                        if (err.response.status === 400) {
                            let errors = err.response.data.data;
                            if (errors.name) {
                                methods.setError("name", { type: "server", message: errors.name[0] });
                            }
                            if (errors.grade) {
                                methods.setError("grade", { type: "server", message: errors.grade[0] });
                            }
                            update("loading", { show: false });
                        } else {
                            update("error", { show: true, code: "A001" });
                        }
                    });
            }
        } else {
            setErrorMessage("Nu ați modificat niciun câmp.");
        }
    };

    return (
        <Box component="main">
            {loading ? (
                <Loading show={true}/>
            ) : (
                <>
                    <PageTitle
                        back={true}
                        actions={
                            editMode ? (
                                <GenericButton
                                    type="close"
                                    onClick={() => {
                                        navigate(-1);
                                    }}
                                />
                            ) : ((user.position === "principal" || tenant.type === "teacher") &&
                                <GenericButton
                                    type="edit"
                                    onClick={() => {
                                        navigate(`/clase/${id}/editare`);
                                    }}
                                />
                            )
                        }
                    >
                        {id !== "" ? (
                            <>
                                Clasa <strong style={{fontWeight: 400}}>{classroom.name} ({getRomanNumber(classroom.grade)})</strong>
                            </>
                        ) : (
                            <>Adaugă clasă</>
                        )}
                    </PageTitle>

                    <BoxContent>
                        <Box width="100%">
                            <FormProvider {...methods}>
                                <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
                                    <Grid container spacing={1.5} alignItems="center">

                                        <FormLabelBox title="Nume:"/>
                                        <FormFieldBox>
                                            {!editMode ? (
                                                classroom.name
                                            ) : (
                                                <FormField
                                                    name="name"
                                                    type="text"
                                                    defaultValue={classroom.name}
                                                    label="Nume"
                                                    autoFocus={true}
                                                    errorMessage={methods.formState.errors.name?.message}
                                                    error={!!methods.formState.errors.name?.message}
                                                />
                                            )}
                                        </FormFieldBox>

                                        <FormLabelBox title="Clasă:"/>
                                        <FormFieldBox>
                                            {!editMode ? (
                                                getRomanNumber(classroom.grade)
                                            ) : (
                                                <FormField
                                                    name="grade"
                                                    type="select"
                                                    defaultValue={classroom.grade < 0 ? "" : classroom.grade}
                                                    options={gradesRomans.map((grade, index) => {
                                                        return {
                                                            label: grade,
                                                            value: index,
                                                        };
                                                    })}
                                                    label="Clasă"
                                                    errorMessage={methods.formState.errors.grade?.message}
                                                    error={!!methods.formState.errors.grade?.message}
                                                />
                                            )}
                                        </FormFieldBox>

                                        {!editMode && (
                                            <>
                                                <FormLabelBox title="Numar elevi:"/>
                                                <FormFieldBox>{classroom.students.length.toString()}</FormFieldBox>
                                            </>
                                        )}

                                        {editMode && (
                                            <>
                                                <FormLabelBox/>
                                                <FormFieldBox>
                                                    <CustomButton submit buttonType="textIcon" bgcolor="dark">
                                                        {id !== "" ? "SALVEAZĂ CLASA" : "ADAUGĂ CLASĂ"}
                                                    </CustomButton>
                                                    <ErrorsList show={errorMessage !== ""}>{errorMessage}</ErrorsList>
                                                </FormFieldBox>
                                            </>
                                        )}
                                    </Grid>
                                </form>
                            </FormProvider>
                        </Box>
                    </BoxContent>
                    {!editMode && (
                        <BoxContent mt={3}>
                            <Box sx={{width: "100%"}}>
                                <Box mb={2} sx={{borderBottom: 1, borderColor: "divider"}}>
                                    <Tabs
                                        value={currentTab}
                                        variant="scrollable"
                                        scrollButtons={!isSmUp}
                                        allowScrollButtonsMobile
                                    >
                                        <Tab
                                            label="Statistici"
                                            value={`/clase/${id}`}
                                            to={`/clase/${id}`}
                                            component={Link}
                                        />
                                        <Tab
                                            label="Teste"
                                            value={`/clase/${id}/teste`}
                                            to={`/clase/${id}/teste`}
                                            component={Link}
                                        />
                                        <Tab
                                            label="Elevi"
                                            value={`/clase/${id}/elevi`}
                                            to={`/clase/${id}/elevi`}
                                            component={Link}
                                        />
                                        {(tenant.type === "school") && (
                                            <Tab
                                                label="Profesori"
                                                value={`/clase/${id}/profesori`}
                                                to={`/clase/${id}/profesori`}
                                                component={Link}
                                            />
                                        )}
                                        <Tab
                                            label="Rapoarte"
                                            value={`/clase/${id}/rapoarte`}
                                            to={`/clase/${id}/rapoarte`}
                                            component={Link}
                                        />
                                    </Tabs>
                                </Box>

                                {location.pathname === `/clase/${id}/elevi` && (
                                    <StudentsList classroom={classroom} updateClassroomHandler={updateClassroomHandler}/>
                                )}
                                {location.pathname === `/clase/${id}/profesori` && (
                                    <TeachersList classroom={classroom} updateClassroomHandler={updateClassroomHandler}/>
                                )}
                                {location.pathname === `/clase/${id}` && (
                                    <DashboardStatistics selectedClassroom={id} pageType="classroom" bgColor="light"/>
                                )}
                                {location.pathname === `/clase/${id}/teste` && (
                                    <>
                                        <TestsHistory fromClassroom={true} pageType="classroom" tabType="history" pageId={id}/>
                                    </>
                                )}
                                {location.pathname === `/clase/${id}/rapoarte` && (
                                    <>
                                        <CustomToolbar>
                                            <GenericButton
                                                type="export"
                                                onClick={() => {
                                                    navigate("/rapoarte-clasa/genereaza", {
                                                        state: {
                                                            type: "classroom",
                                                            classroom_id: id,
                                                        }
                                                    });
                                                }}
                                            />
                                        </CustomToolbar>
                                        <ReportsList type="classroom" classroomId={id} />
                                    </>
                                )}
                            </Box>
                        </BoxContent>
                    )}
                </>
            )}
        </Box>
    );
};

export default Classroom;
